import React, { ReactNode, MouseEvent } from 'react';
import { makeStyles, Typography, useTheme, Dialog, Link, DialogProps } from '@material-ui/core';
import { LabelButton } from '../Buttons/LabelButton';

const styles = makeStyles(({ spacing }) => ({
  text: {
    textAlign: 'center',
    marginBottom: spacing(4),
  },
  modal: {
    padding: spacing(6),
    borderRadius: '16px',
  },
  linkText: {
    textDecoration: 'underline',
    textAlign: 'center',
    marginBottom: spacing(6),
    fontSize: spacing(4),
    textUnderlineOffset: '6px',
    backgroundColor: 'transparent',
  },
  icon: {
    display: ' flex',
    justifyContent: 'center',
    margin: `${spacing(5)}px auto`,
  },
}));

export interface MessageModalCopy {
  title: string;
  bodyText: string;
  closeButtonText: string;
}

export interface LinkToPage {
  href: string;
  linkText: string;
}

export type CloseReason = 'backdropClick' | 'escapeKeyDown';

interface ControlledMessageModalProps extends DialogProps {
  open: boolean;
  onClose: (event: MouseEvent<HTMLButtonElement>, reason?: CloseReason) => void;
  icon: ReactNode;
  messageCopy: MessageModalCopy;
  link?: LinkToPage;
  ariaLabel?: string;
}

export const ControlledMessageModal = ({
  open,
  onClose,
  icon,
  messageCopy: { title, bodyText, closeButtonText },
  link,
  ariaLabel,
}: ControlledMessageModalProps) => {
  const classes = styles();
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      classes={{ paper: classes.modal }}
      aria-labelledby="modal-title"
      aria-describedby="modal-content"
      aria-label={ariaLabel}
    >
      <Typography id="modal-title" variant="h1" component="h1" className={classes.text}>
        {title}
      </Typography>
      <div className={classes.icon}>{icon}</div>
      <Typography id="modal-content" variant="body1" className={classes.text}>
        {bodyText}
      </Typography>
      {link && (
        <Link
          rel="noopener"
          target="_blank"
          href={link?.href}
          id="modal-content"
          className={classes.linkText}
          variant="h4"
        >
          {link?.linkText}
        </Link>
      )}
      <LabelButton
        onClick={onClose}
        aria-label="close"
        data-cy="close"
        label={closeButtonText}
        labelColor={theme.palette.text.primary}
      />
    </Dialog>
  );
};
